import app from 'containers/app'
import { createAction } from 'redux-actions'
import { constants } from './constants'
import { api } from './api'
import { actions as UserActions } from 'app/modules/user'
import { captureException } from 'utils/SentryHelpers'
import * as selectors from './selectors'
import * as selectorsUI from './selectorsUI'
import memoize from 'app/api/memoize'
import { mergeAll } from 'ramda'
import { getDriftCutover } from 'app/modules/access/api'

export const createOrgStarted = createAction(constants.CREATE_ORG_STARTED)
export const createOrgFailed = createAction(constants.CREATE_ORG_FAILED)
export const fetchOrgStarted = createAction(constants.FETCH_ORG_STARTED)
export const fetchOrgFailed = createAction(constants.FETCH_ORG_FAILED)
export const updateOrgStarted = createAction(constants.UPDATE_ORG_STARTED)
export const updateOrgFailed = createAction(constants.UPDATE_ORG_FAILED)
export const receiveOrg = createAction(constants.RECEIVE_ORG)
export const receiveOrgProperties = createAction(constants.RECEIVE_ORG_PROPERTIES)
export const receiveOrgCutover = createAction(constants.RECEIVE_ORG_CUTOVER, payload => payload, (_, meta) => meta)
export const checkOrgHandleExistenceStarted = createAction(constants.CHECK_ORG_HANDLE_EXISTENCE_STARTED)
export const checkOrgHandleExistenceFailed = createAction(constants.CHECK_ORG_HANDLE_EXISTENCE_FAILED)
export const receiveOrgHandleExistence = createAction(constants.RECEIVE_ORG_HANDLE_EXISTENCE)
export const setOrgHandleUI = createAction(constants.SET_ORG_HANDLE_UI)
export const receiveUserScopes = createAction(constants.RECEIVE_USER_SCOPES)

export const patchOrgProperties = createAction(constants.PATCH_ORG_PROPERTIES)
export const patchOrgPropertiesPending = createAction(constants.PATCH_ORG_PROPERTIES_PENDING)
export const patchOrgPropertiesSuccess = createAction(constants.PATCH_ORG_PROPERTIES_SUCCESS)
export const patchOrgPropertiesFailure = createAction(constants.PATCH_ORG_PROPERTIES_FAILURE)

export const sendExistingUsersInvitationsViaEmailStarted = createAction(
  constants.SEND_EXISTING_USERS_INVITATIONS_VIA_EMAIL_STARTED
)
export const sendExistingUsersInvitationsViaEmailCompleted = createAction(
  constants.SEND_EXISTING_USERS_INVITATIONS_VIA_EMAIL_COMPLETED
)
export const sendExistingUsersInvitationsViaEmailFailed = createAction(
  constants.SEND_EXISTING_USERS_INVITATIONS_VIA_EMAIL_FAILED
)

export const sendInvitationsViaEmailStarted = createAction(constants.SEND_INVITATIONS_VIA_EMAIL_STARTED)
export const sendInvitationsViaEmailCompleted = createAction(constants.SEND_INVITATIONS_VIA_EMAIL_COMPLETED)
export const sendInvitationsViaEmailFailed = createAction(constants.SEND_INVITATIONS_VIA_EMAIL_FAILED)

export const sendInvitationsViaSlackStarted = createAction(constants.SEND_INVITATIONS_VIA_SLACK_STARTED)
export const sendInvitationsViaSlackCompleted = createAction(constants.SEND_INVITATIONS_VIA_SLACK_COMPLETED)
export const sendInvitationsViaSlackFailed = createAction(constants.SEND_INVITATIONS_VIA_SLACK_FAILED)

export const toggleChillbotCompleted = createAction(constants.TOGGLE_CHILLBOT_COMPLETED)
export const toggleChillbotFailed = createAction(constants.TOGGLE_CHILLBOT_FAILED)

export const fetchEndUserCount = createAction(constants.FETCH_END_USER_COUNT)
export const fetchEndUserCountPending = createAction(constants.FETCH_END_USER_COUNT_PENDING)
export const fetchEndUserCountSuccess = createAction(constants.FETCH_END_USER_COUNT_SUCCESS)
export const fetchEndUserCountFailed = createAction(constants.FETCH_END_USER_COUNT_FAILED)

export const hasLimitedContactsMktEmail = createAction(constants.HAS_LIMITED_CONTACT_MKT_EMAIL)
export const hasLimitedContactsMktEmailPending = createAction(constants.HAS_LIMITED_CONTACT_MKT_EMAIL_PENDING)
export const hasLimitedContactsMktEmailSuccess = createAction(constants.HAS_LIMITED_CONTACT_MKT_EMAIL_SUCCESS)
export const hasLimitedContactsMktEmailFailed = createAction(constants.HAS_LIMITED_CONTACT_MKT_EMAIL_FAILED)

export const createOrg = () => dispatch => {
  dispatch(createOrgStarted())
  return api
    .create()
    .then(data => dispatch(receiveOrg(data)))
    .catch(error => {
      captureException(error)
      dispatch(createOrgFailed(error))
      throw error
    })
}

export const fetchOrg = memoize(() => (dispatch, getState) => {
  return dispatch(UserActions.fetchCurrentUser()).then(() => {
    const orgId = selectors.orgId(getState())
    dispatch(fetchOrgStarted(orgId))
    return api
      .fetch(orgId)
      .then(data => dispatch(receiveOrg(data)))
      .then(() => dispatch(fetchOrgProperties(orgId)))
      .catch(error => {
        captureException(error)
        dispatch(fetchOrgFailed(error))
        throw error
      })
  })
})

export const fetchOrgProperties = memoize(() => (dispatch, getState) => {
  const orgId = selectors.orgId(getState())

  return api
    .fetchProperties(orgId)
    .then(data => {
      dispatch(receiveOrgProperties(mergeAll([data.orgProperties, data.authOrgProperties])))
    })
    .catch(error => {
      captureException(error)
      dispatch(fetchOrgFailed(error))
      throw error
    })
})

export const fetchOrgCutover = memoize((cutoverName) => (dispatch, getState) => {
  const orgId = selectors.orgId(getState())
  return getDriftCutover({ orgId, cutoverName })
    .then(data => dispatch(receiveOrgCutover(data[cutoverName], cutoverName)))
    .catch(error => {
      captureException(error)
      throw error
    })
})

export const updateOrg = updates => (dispatch, getState) => {
  return dispatch(UserActions.fetchCurrentUser()).then(() => {
    const orgId = selectors.orgId(getState())
    dispatch(updateOrgStarted(orgId))
    return api
      .update(orgId, updates)
      .then(data => {
        dispatch(receiveOrg(data))
        app.actions.NotificationActions.pushNotification({
          content: 'Your company information has been saved!',
          variant: 'green',
        })
      })
      .catch(error => {
        app.actions.NotificationActions.pushNotification({
          content: 'An error ocurred while updating your company information. Please try again later.',
          variant: 'red',
        })
        dispatch(updateOrgFailed(error))
        captureException(error)
        throw error
      })
  })
}

export const batchUpdateUserScopes = updates => (dispatch, getState) => {
  return dispatch(UserActions.fetchCurrentUser()).then(() => {
    const orgId = selectors.orgId(getState())
    return api
      .batchUpdateUserScopes(orgId, updates)
      .then(() => {
        dispatch(receiveUserScopes(updates))
        app.actions.NotificationActions.pushNotification({
          content: 'Your updates have been saved!',
          variant: 'green',
        })
      })
      .catch(error => {
        app.actions.NotificationActions.pushNotification({
          content: 'An error ocurred while updating your user information. Please try again later.',
          variant: 'red',
        })
        captureException(error)
        throw error
      })
  })
}

export const checkOrgHandle = () => (dispatch, getState) => {
  const orgHandle = selectorsUI.orgHandle(getState())
  if (!orgHandle.length > 0) return
  dispatch(checkOrgHandleExistenceStarted())
  return api.checkHandle(orgHandle).then(res => {
    if (res.status === 404) {
      return dispatch(receiveOrgHandleExistence({ valid: true }))
    } else if (res.status === 200) {
      return dispatch(receiveOrgHandleExistence({ valid: false }))
    } else {
      captureException(new Error(`Unable to check org handle existence for handle ${orgHandle}`))
      dispatch(checkOrgHandleExistenceFailed())
    }
  })
}

export const sendExistingUsersInvitationsViaEmail = userId => (dispatch, getState) => {
  const orgId = selectors.orgId(getState())
  const payload = { invitedUsers: [userId] }
  dispatch(sendExistingUsersInvitationsViaEmailStarted())
  return api.sendExistingInvitationsViaEmail(orgId, payload).then(res => {
    if (res.ok) {
      app.actions.NotificationActions.pushNotification({ content: 'Your invitations have been sent!' })
      return dispatch(sendExistingUsersInvitationsViaEmailCompleted())
    } else {
      app.actions.NotificationActions.pushNotification({
        variant: 'red',
        content: res.message,
      })
      captureException(new Error(`Unable to invite users`))
      dispatch(sendExistingUsersInvitationsViaEmailFailed())
    }
  })
}

export const sendInvitationsViaEmail = invitations => (dispatch, getState) => {
  const orgId = selectors.orgId(getState())
  dispatch(sendInvitationsViaEmailStarted())
  return api.sendInvitationsViaEmail(orgId, invitations).then(res => {
    if (res.ok) {
      app.actions.NotificationActions.pushNotification({ content: 'Your invitations have been sent!' })
      return dispatch(sendInvitationsViaEmailCompleted())
    } else {
      app.actions.NotificationActions.pushNotification({
        variant: 'red',
        content: res.message,
      })
      captureException(new Error(`Unable to invite users`))
      dispatch(sendInvitationsViaEmailFailed())
    }
  })
}

export const sendInvitationsViaSlack = invitations => (dispatch, getState) => {
  const orgId = selectors.orgId(getState())
  dispatch(sendInvitationsViaSlackStarted())
  return api.sendInvitationsViaSlack(orgId, invitations).then(res => {
    if (res.ok) {
      app.actions.NotificationActions.pushNotification({
        content: 'Your invites have been sent!',
      })
      return dispatch(sendInvitationsViaSlackCompleted())
    } else {
      app.actions.NotificationActions.pushNotification({
        content: res.message,
        variant: 'red',
      })
      captureException(new Error(`Unable to invite users via slack`))
      dispatch(sendInvitationsViaSlackFailed())
    }
  })
}

export const toggleChillbot = chillbotStatus => dispatch => {
  return api.toggleChillbot(chillbotStatus).then(res => {
    if (res.ok) {
      dispatch(toggleChillbotCompleted())
    } else {
      dispatch(toggleChillbotFailed())
    }
    return dispatch(fetchOrgProperties())
  })
}

export const fetchDriftHelpStatus = () => (dispatch, getState) => {
  const orgId = selectors.orgId(getState())

  return api.getDriftHelpStatus(orgId)
    .then(response => response.enabled)
}



// WEBPACK FOOTER //
// ./src/scripts/app/modules/org/actions.js